/**
 * Apple-style Items List Component
 *
 * Renders order line items in a clean, Apple-inspired list format.
 * Optimized for touch interactions with larger targets and prominent visual hierarchy.
 * Shows: Large thumbnail, Item Name, SKU, Quantity, Rate, Amount.
 * Click the "..." icon to edit item details in modal.
 */

import React, { useState, useMemo, useCallback } from 'react';
import type { DocFieldSchema } from '@/lib/erp/meta';
import { ChildRowEditModal } from '@/components/child-table/ChildRowEditModal';
import { useQuery } from '@tanstack/react-query';
import { erp } from '@/lib/erp';
import { useDynamicView } from '@/contexts/DynamicViewContext';
import { useResolveFieldLabel } from '@/lib/views/labels';
import { useLanguage } from '@/i18n/useLanguage';
import { getUITranslation } from '@/lib/views/labels/translations';
import { LinkFieldSearch } from '@/components/filter-builder/LinkFieldSearch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LayoutGrid, Minus, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { OrderItemBundleSection } from '@/features/selling/sales-order-detail/components/OrderItemBundleSection';
import { OrderItemPersonalizationCard } from '@/features/selling/sales-order-detail/components/OrderItemPersonalizationCard';
import { cn } from '@/lib/utils';
import { BundleBadge } from '@/features/selling/sales-order-detail/components/BundleBadge';
import { POSProductPickerModal } from './POSProductPickerModal';
import { frappeMethod } from '@/lib/erp/utils';
import { useFrappeFileUrl } from '@/lib/utils/frappeUrl';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { SalesOrder, SalesOrderItemCustomization } from '@/lib/erp/types/order';
import { correlateOrderBundleData } from '@/features/selling/utils/orderBundleData';
import { formatCurrency } from './ShopifyItemRow';
import type { ItemRow } from './ShopifyItemRow';
import { CustomizationFormModal } from '@/features/customization/components/CustomizationFormModal';

interface ShopifyItemsTableProps {
  field: DocFieldSchema;
  value: any; // Accept flexible shape, normalize internally
  onChange: (value: any) => void; // Keep consistent with other child tables
  formData: Record<string, any>;
  disabled?: boolean;
  resetKey?: string | number;
  viewMode?: 'system' | 'experience'; // Optional for future system view compatibility
  isNew?: boolean; // Whether this is a new document (create mode)
  onCustomizationsChange?: (customizations: SalesOrderItemCustomization[]) => void;
}

/**
 * Format number
 */
function formatNumber(value: any): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '—';
  return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

/**
 * Render product info with thumbnail, name, and code (Apple-style)
 * Larger sizing for better touch support and visual prominence
 */
function renderProductInfo(
  row: ItemRow,
  getFieldValue: (row: any, fieldname: string) => any,
  toAbsoluteUrl: (path?: string | null) => string | null,
  itemRank?: number,
  skuLabel?: string,
  isBundle?: boolean
): React.ReactNode {
  // Get product name - use getFieldValue to properly access fields
  // Prioritize item_name, but check other possible name fields
  // IMPORTANT: Never use item_code as product name - item_code is the SKU
  const productName = getFieldValue(row, 'item_name') ||
    getFieldValue(row, 'description') ||
    getFieldValue(row, 'item_description') ||
    null;

  // SKU is always from item_code - never use this as product name
  const sku = getFieldValue(row, 'item_code');

  // Try multiple image field names (in priority order)
  const rawImageUrl = getFieldValue(row, 'image') ||
    getFieldValue(row, 'item_image') ||
    getFieldValue(row, 'thumbnail');

  // Convert relative path to absolute URL for proper loading
  const imageUrl = toAbsoluteUrl(rawImageUrl);

  // Display name for alt text
  const displayName = productName || (sku ? `Item ${sku}` : "Unnamed item");

  // Initials fallback when no image
  const initials = (() => {
    const parts = displayName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return displayName.slice(0, 2).toUpperCase();
  })();
  const initialsHue = (() => {
    let hash = 0;
    for (let i = 0; i < displayName.length; i++) hash = displayName.charCodeAt(i) + ((hash << 5) - hash);
    return Math.abs(hash) % 360;
  })();

  const isTopItem = itemRank !== undefined && itemRank <= 3;

  return (
    <div className="flex items-center gap-4 min-w-0 flex-1">
      {/* Thumbnail - 72px × 72px (balanced size for touch and prominence) */}
      <div className="w-[72px] h-[72px] rounded-sm bg-muted overflow-hidden flex-shrink-0 border border-border">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={displayName}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-base font-semibold select-none"
            style={{ backgroundColor: `hsl(${initialsHue}, 45%, 88%)`, color: `hsl(${initialsHue}, 50%, 35%)` }}
          >
            {initials}
          </div>
        )}
      </div>

      {/* Product Name and SKU */}
      <div className="flex flex-col min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {/* Product Name - always displayed (from item_name, never item_code) */}
          {productName ? (
            <span className="text-sm font-normal text-gray-900 dark:text-gray-100 leading-snug">
              {productName}
            </span>
          ) : (
            // Fallback: show placeholder if no product name available
            <span className="text-sm font-normal text-gray-400 dark:text-gray-500 italic leading-snug">
              {sku ? `Item ${sku}` : "Unnamed item"}
            </span>
          )}
          {/* Rank Badge - for top 3 items */}
          {isTopItem && (
            <Badge
              variant="outline"
              className={cn(
                "text-[10px] px-2 py-0.5 h-5 font-medium",
                itemRank === 1
                  ? "border-primary/30 text-primary"
                  : "border-border/40 text-muted-foreground"
              )}
            >
              #{itemRank}
            </Badge>
          )}
          {/* Bundle Badge */}
          {isBundle && <BundleBadge />}
        </div>
        {/* SKU - displayed below product name (from item_code) */}
        {sku && (
          <span className="text-sm text-gray-700 dark:text-gray-300 mt-1">
            {skuLabel || 'SKU'}: {sku}
          </span>
        )}
      </div>
    </div>
  );
}

export function ShopifyItemsTable({
  field,
  value,
  onChange,
  formData,
  disabled,
  resetKey,
  viewMode: _viewMode = 'experience', // Optional for future system view compatibility
  isNew = false,
  onCustomizationsChange,
}: ShopifyItemsTableProps) {
  const childDoctype = field.options || '';
  const [productSearchValue, setProductSearchValue] = useState<string | null>(null);
  const [isPOSModalOpen, setIsPOSModalOpen] = useState(false);

  // Pending item awaiting customization — defers row creation until modal completes
  const [pendingItem, setPendingItem] = useState<{
    itemCode: string;
    itemDetails: {
      item_name: string;
      description: string;
      standard_rate: number;
      stock_uom: string;
      primary_image: string | null;
    };
  } | null>(null);

  // TODO(pricing-rules): State for pricing rule warning dialog - not yet implemented
  // See: handleRateChange for where validation should be added
  const [pricingWarning, setPricingWarning] = useState<{
    show: boolean;
    index: number;
    field: 'qty' | 'rate';
    attemptedValue: number;
    validValue: number;
    message: string;
  } | null>(null);

  // Local state for rate inputs (allows typing before blur)
  const [editingRates, setEditingRates] = useState<Map<number, string>>(new Map());

  // Get view context for label resolution
  const { mode, style, enableSystemTranslations } = useDynamicView();
  const resolveLabel = useResolveFieldLabel();
  const { language } = useLanguage();

  // Convert relative file paths to absolute URLs for image loading
  const toAbsoluteUrl = useFrappeFileUrl();

  // Normalize value to rows array (handles various shapes)
  const items: ItemRow[] = Array.isArray(value)
    ? value
    : value?.rows ?? value?.data ?? [];

  // Extract item codes for POS modal "already added" indicator
  const selectedItemCodes = useMemo(
    () => items.map((item) => item.item_code || '').filter(Boolean),
    [items]
  );

  const currency = formData.currency || 'THB';

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);

  // Fetch child doctype schema
  const { data: childSchema } = useQuery({
    queryKey: ['meta', childDoctype],
    queryFn: () => erp.meta.getDocTypeSchema(childDoctype),
    enabled: !!childDoctype,
  });

  // Determine which fields to display (prefer in_list_view, fallback to common fields)
  const displayFields = useMemo(() => {
    if (!childSchema) return [];

    // First try in_list_view fields
    const listViewFields = childSchema.fields.filter(f => {
      const v = (f as any).in_list_view;
      return (v === 1 || v === true || v === '1') && f.hidden !== 1;
    });

    if (listViewFields.length > 0) {
      return listViewFields;
    }

    // Fallback to common item fields
    const commonFields = ['item_code', 'item_name', 'qty', 'rate', 'amount'];
    return childSchema.fields.filter(f =>
      commonFields.includes(f.fieldname) && f.hidden !== 1
    );
  }, [childSchema]);

  // Get field value from row
  const getFieldValue = useCallback((row: any, fieldname: string): any => {
    return row[fieldname];
  }, []);

  // Phase 1: Item ranking logic
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const aAmount = getFieldValue(a, 'amount') || getFieldValue(a, 'total') || 0;
      const bAmount = getFieldValue(b, 'amount') || getFieldValue(b, 'total') || 0;
      return Number(bAmount) - Number(aAmount);
    });
  }, [items, getFieldValue]);

  // Resolve labels for child doctype fields (MUST be before early returns to maintain hook order)
  const resolvedLabels = useMemo(() => {
    if (!childSchema || !childDoctype) {
      return {
        itemCode: 'SKU',
        qty: 'Quantity',
        amount: 'Total',
        discountAmount: 'Item Discount',
      };
    }

    const getFieldLabel = (fieldname: string, fallback: string) => {
      const field = childSchema.fields.find((f: DocFieldSchema) => f.fieldname === fieldname);
      if (field && resolveLabel) {
        return resolveLabel(childDoctype, field, mode, style, enableSystemTranslations).label;
      }
      return fallback;
    };

    return {
      itemCode: getFieldLabel('item_code', 'SKU'),
      qty: getFieldLabel('qty', 'Quantity'),
      amount: getFieldLabel('amount', 'Total'),
      discountAmount: getFieldLabel('discount_amount', 'Item Discount'),
    };
  }, [childSchema, childDoctype, resolveLabel, mode, style, enableSystemTranslations]);

  // Resolve UI labels (MUST be before early returns to maintain hook order)
  const uiLabels = useMemo(() => {
    const getUI = (key: string, fallback: string) => {
      return getUITranslation(key, language) || fallback;
    };

    return {
      totalItems: getUI('total_items', 'Total Items'),
      item: getUI('item', 'item'),
      items: getUI('items', 'items'),
      unit: getUI('unit', 'unit'),
      units: getUI('units', 'units'),
      total: getUI('total', 'total'),
      subtotal: getUI('subtotal', 'Subtotal'),
      itemsSubtotal: getUI('items_subtotal', 'Items Subtotal'),
      itemsTotal: getUI('items_total', 'Items Total'),
      averagePrice: getUI('average_price', 'Average Price'),
      perItem: getUI('per_item', 'per item'),
      beforeDiscounts: getUI('before_discounts', 'Before discounts'),
      appliedToItems: getUI('applied_to_items', 'Applied to items'),
      afterDiscount: getUI('after_discount', 'after discount'),
      searchProducts: getUI('search_products', 'Search products...'),
      addProduct: getUI('add_product', 'Add Product'),
      removeItem: getUI('remove_item', 'Remove'),
      noItemsYet: getUI('no_items_yet', 'No items added yet'),
      searchAndAddItems: getUI('search_and_add_items', 'Search and add items to this order'),
      moreDetails: getUI('more_details', 'More details'),
      rate: getUI('rate', 'Rate'),
      pricingRuleViolation: getUI('pricing_rule_violation', 'Pricing Rule Violation'),
      pricingRuleMessage: getUI('pricing_rule_message', 'The entered value violates pricing rules. The value has been reverted.'),
      ok: getUI('ok', 'OK'),
    };
  }, [language]);

  // Bundle data correlation — groups packed_items by parent item row
  const bundleDataMap = useMemo(() => {
    return correlateOrderBundleData(formData as SalesOrder);
  }, [formData]);

  // Finalize adding an item row (called after customization modal or directly if no template)
  const finalizeAddItem = useCallback((
    itemCode: string,
    itemDetails: { item_name: string; description: string; standard_rate: number; stock_uom: string; primary_image: string | null },
    customizations?: SalesOrderItemCustomization[],
  ) => {
    const tempName = `new-so-item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    const newRow: ItemRow = {
      name: tempName,
      idx: items.length + 1,
      item_code: itemCode,
      item_name: itemDetails.item_name || itemCode,
      description: itemDetails.description || '',
      image: itemDetails.primary_image || '',
      qty: 1,
      rate: itemDetails.standard_rate || 0,
      amount: itemDetails.standard_rate || 0,
      uom: itemDetails.stock_uom || 'Nos',
      stock_uom: itemDetails.stock_uom || 'Nos',
      conversion_factor: 1,
      __islocal: 1,
      __unsaved: 1,
    };

    const newItems = [...items, newRow];
    onChange(newItems);

    // Append customization entries if any
    if (customizations && customizations.length > 0 && onCustomizationsChange) {
      const entries = customizations.map((c) => ({
        ...c,
        parent_item_row: tempName,
      }));
      const existing: SalesOrderItemCustomization[] = formData.item_customizations ?? [];
      onCustomizationsChange([...existing, ...entries]);
    }

    setProductSearchValue(null);
    setPendingItem(null);
  }, [items, onChange, onCustomizationsChange, formData.item_customizations]);

  // Handle adding a new item from product search
  const handleAddItem = useCallback(async (itemCode: string | null) => {
    if (!itemCode) return;

    try {
      const itemDetails = await frappeMethod<{
        item_code: string;
        item_name: string;
        description: string;
        standard_rate: number;
        stock_uom: string;
        variant_of: string | null;
        primary_image: string | null;
      }>('zaviago_backend.api.item_list.get_item_with_primary_image', {
        item_code: itemCode,
      });

      // Defer to customization modal — it will auto-skip if no template
      setPendingItem({ itemCode, itemDetails });
    } catch {
      // Fallback: add item with minimal info if API fails
      const newRow: ItemRow = {
        idx: items.length + 1,
        item_code: itemCode,
        item_name: itemCode,
        qty: 1,
        rate: 0,
        amount: 0,
        __islocal: 1,
        __unsaved: 1,
      };
      const newItems = [...items, newRow];
      onChange(newItems);
      setProductSearchValue(null);
    }
  }, [items, onChange]);

  // Handle adding item from POS modal — customization already handled inside POS modal
  const handleAddItemFromPOS = useCallback(async (itemCode: string, customizations?: SalesOrderItemCustomization[]) => {
    if (!itemCode) return;

    try {
      const itemDetails = await frappeMethod<{
        item_code: string;
        item_name: string;
        description: string;
        standard_rate: number;
        stock_uom: string;
        variant_of: string | null;
        primary_image: string | null;
      }>('zaviago_backend.api.item_list.get_item_with_primary_image', {
        item_code: itemCode,
      });

      // Skip pending customization modal — POS modal already handled it
      finalizeAddItem(itemCode, itemDetails, customizations);
    } catch {
      const newRow: ItemRow = {
        idx: items.length + 1,
        item_code: itemCode,
        item_name: itemCode,
        qty: 1,
        rate: 0,
        amount: 0,
        __islocal: 1,
        __unsaved: 1,
      };
      const newItems = [...items, newRow];
      onChange(newItems);
    }
  }, [items, onChange, finalizeAddItem]);

  // Handle removing an item by index — also cleans up associated customizations
  const handleRemoveItem = useCallback((index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const removedItem = items[index];
    const newItems = items.filter((_, i) => i !== index);
    const reindexedItems = newItems.map((item, i) => ({
      ...item,
      idx: i + 1,
    }));
    onChange(reindexedItems);

    // Remove associated customization entries
    if (removedItem?.name && onCustomizationsChange) {
      const existing: SalesOrderItemCustomization[] = formData.item_customizations ?? [];
      const filtered = existing.filter((c) => c.parent_item_row !== removedItem.name);
      if (filtered.length !== existing.length) {
        onCustomizationsChange(filtered);
      }
    }
  }, [items, onChange, onCustomizationsChange, formData.item_customizations]);

  // Handle removing an item by item_code (for POS modal)
  const handleRemoveItemByCode = useCallback((itemCode: string) => {
    const index = items.findIndex((item) => item.item_code === itemCode);
    if (index === -1) return;
    const newItems = items.filter((_, i) => i !== index);
    // Re-index items
    const reindexedItems = newItems.map((item, i) => ({
      ...item,
      idx: i + 1,
    }));
    onChange(reindexedItems);
  }, [items, onChange]);

  // Handle inline quantity change
  const handleQuantityChange = useCallback((index: number, newQty: number) => {
    if (disabled || newQty < 0) return;

    const newItems = [...items];
    const item = { ...newItems[index] };
    const rate = item.rate || 0;

    item.qty = newQty;
    item.amount = newQty * rate;
    newItems[index] = item;

    onChange(newItems);
  }, [items, onChange, disabled]);

  // Handle inline rate change with pricing rule validation
  // TODO(pricing-rules): Add ERPNext pricing rule validation API call here
  // When implemented, call backend to validate rate and show pricingWarning dialog if violated
  const handleRateChange = useCallback(async (index: number, newRate: number) => {
    if (disabled || newRate < 0) return;

    const item = items[index];
    const qty = item.qty || 1;

    const newItems = [...items];
    const updatedItem = { ...newItems[index] };
    updatedItem.rate = newRate;
    updatedItem.amount = qty * newRate;
    newItems[index] = updatedItem;

    onChange(newItems);
  }, [items, onChange, disabled]);

  // NOTE: editing rate state is managed directly via setEditingRates in JSX handlers

  // Handle opening the more details modal
  const handleOpenDetails = useCallback((index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    setEditingRowIndex(index);
    setModalOpen(true);
  }, [disabled]);

  // Handle modal row change
  const handleModalRowChange = (updatedRow: any) => {
    if (editingRowIndex !== null) {
      const newItems = [...items];
      newItems[editingRowIndex] = updatedRow;
      onChange(newItems);
    }
    setModalOpen(false);
    setEditingRowIndex(null);
  };

  const editingRow = editingRowIndex !== null ? items[editingRowIndex] : null;

  // Check for loading or empty states
  const isLoading = !childSchema || displayFields.length === 0;
  const isEmpty = items.length === 0;

  // Calculate totals (only needed when we have items)
  // Calculate original subtotal before item discounts (for percentage calculation)
  // Use price_list_rate (original price) if available, otherwise use rate
  const originalSubtotalBeforeDiscounts = items.reduce((sum, item) => {
    const priceListRate = getFieldValue(item, 'price_list_rate') || 0;
    const rate = getFieldValue(item, 'rate') || getFieldValue(item, 'price') || 0;
    // Use price_list_rate as original price, fallback to rate if not available
    const originalPrice = priceListRate > 0 ? Number(priceListRate) : Number(rate);
    const qty = getFieldValue(item, 'qty') || getFieldValue(item, 'quantity') || 0;
    return sum + (originalPrice * Number(qty));
  }, 0);

  // The 'amount' field in items is typically the final amount after item-level discounts
  const subtotal = items.reduce((sum, item) => {
    const amount = getFieldValue(item, 'amount') || getFieldValue(item, 'total') || 0;
    return sum + (typeof amount === 'number' ? amount : parseFloat(String(amount)) || 0);
  }, 0);

  // Calculate item-level discounts from items table
  // Item discounts can be:
  // 1. Explicit discount_amount field per item
  // 2. Calculated from rate * qty - amount (if amount is already discounted)
  // 3. Or discount_percentage applied
  const itemDiscountAmount = items.reduce((sum, item) => {
    // Try explicit discount_amount field first
    const itemDiscount = getFieldValue(item, 'discount_amount') || 0;
    if (itemDiscount > 0) {
      return sum + Number(itemDiscount);
    }
    
    // If no explicit discount, calculate from rate * qty - amount
    // (assuming amount is already discounted)
    const rate = getFieldValue(item, 'rate') || getFieldValue(item, 'price') || 0;
    const qty = getFieldValue(item, 'qty') || getFieldValue(item, 'quantity') || 0;
    const amount = getFieldValue(item, 'amount') || getFieldValue(item, 'total') || 0;
    const originalAmount = Number(rate) * Number(qty);
    const calculatedDiscount = Math.max(0, originalAmount - Number(amount));
    
    return sum + calculatedDiscount;
  }, 0);

  // Calculate items total after item-level discount
  // Subtotal already includes discounts (amount field is after discount)
  // So itemsTotalAfterDiscount = subtotal (no need to subtract again)
  const itemsTotalAfterDiscount = subtotal;

  // Phase 1: Data Enhancement - Order-level calculations
  // Use original subtotal (before item discounts) for item percentages
  // This ensures percentages add up to 100% of original items value
  const itemsSubtotal = originalSubtotalBeforeDiscounts; // This is the base for item percentage calculations
  const totalItems = items.length;
  const totalQuantity = items.reduce((sum, item) => {
    const qty = getFieldValue(item, 'qty') || getFieldValue(item, 'quantity') || 0;
    return sum + Number(qty);
  }, 0);
  // Average price should be based on subtotal (items total), not grand total
  const averagePricePerItem = itemsSubtotal > 0 && totalItems > 0
    ? itemsSubtotal / totalItems
    : 0;



  // Get rank for each item
  const getItemRank = (item: ItemRow): number => {
    const itemIdx = item.idx !== undefined ? item.idx : items.indexOf(item);
    const sortedIndex = sortedItems.findIndex(sortedItem => {
      const sortedIdx = sortedItem.idx !== undefined ? sortedItem.idx : items.indexOf(sortedItem);
      return sortedIdx === itemIdx;
    });
    return sortedIndex >= 0 ? sortedIndex + 1 : items.length + 1;
  };

  // Phase 1: Totals context calculations (items section only)
  // Items total after item discount as percentage of original subtotal before discounts
  const itemsTotalPercentage = originalSubtotalBeforeDiscounts > 0
    ? ((itemsTotalAfterDiscount / originalSubtotalBeforeDiscounts) * 100).toFixed(1)
    : "100.0";

  return (
    <>
      {/* Loading state */}
      {isLoading && (
        <div className="rounded-xl border border-border bg-card p-8">
          <div className="text-sm text-muted-foreground text-center">
            Loading items table...
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && isEmpty && (
        <div className="rounded-xl border border-border bg-card p-8">
          {isNew && !disabled ? (
            <div className="flex flex-col items-center gap-4">
              <div className="text-sm text-muted-foreground text-center">
                {uiLabels.noItemsYet}
              </div>
              <div className="text-xs text-muted-foreground text-center mb-2">
                {uiLabels.searchAndAddItems}
              </div>
              <div className="w-full max-w-md">
                <div className="flex items-center gap-2">
                  <LinkFieldSearch
                    doctype="Item"
                    value={productSearchValue}
                    onChange={handleAddItem}
                    placeholder={uiLabels.searchProducts}
                    defaultFilters={{ disabled: 0 }}
                    className="flex-1"
                    fetchInitialItems={true}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setIsPOSModalOpen(true)}
                    title="Browse products"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground text-center">
              No items in this order
            </div>
          )}
        </div>
      )}

      {/* Apple-style List Container - Larger, more prominent (only show when we have items) */}
      {!isLoading && !isEmpty && (
      <div className="rounded-xl border border-border bg-card overflow-clip">
        {/* Product Search - Only shown for new orders */}
        {isNew && !disabled && (
          <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <LinkFieldSearch
                doctype="Item"
                value={productSearchValue}
                onChange={handleAddItem}
                placeholder={uiLabels.searchProducts}
                defaultFilters={{ disabled: 0 }}
                className="flex-1"
                fetchInitialItems={true}
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsPOSModalOpen(true)}
                title="Browse products"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Phase 3: Order-Level Context Header */}
        <div className="px-6 sm:px-8 pt-8 pb-8 border-b border-border">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {/* Total Items - Left */}
            <div className="flex flex-col space-y-1.5">
              <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                {uiLabels.totalItems}
              </span>
              <span className="text-base font-medium text-foreground leading-tight">
                {totalItems} {totalItems === 1 ? uiLabels.item : uiLabels.items}
              </span>
              <span className="text-xs text-muted-foreground font-normal">
                {totalQuantity} {totalQuantity === 1 ? uiLabels.unit : uiLabels.units} {uiLabels.total}
              </span>
            </div>

            {/* Items Subtotal (before discounts) - Center */}
            <div className="flex flex-col space-y-1.5 items-center sm:items-center">
              <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                {uiLabels.subtotal}
              </span>
              <span className="text-base font-medium text-foreground leading-tight">
                {formatCurrency(originalSubtotalBeforeDiscounts, currency)}
              </span>
              {itemDiscountAmount > 0 && (
                <span className="text-xs text-muted-foreground font-normal">
                  {formatCurrency(itemDiscountAmount, currency)} {resolvedLabels.discountAmount.toLowerCase()}
                </span>
              )}
            </div>

            {/* Average Price - Right */}
            <div className="flex flex-col space-y-1.5 items-end sm:items-end">
              <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                {uiLabels.averagePrice}
              </span>
              <span className="text-base font-medium text-foreground leading-tight">
                {formatCurrency(averagePricePerItem, currency)}
              </span>
              <span className="text-xs text-muted-foreground font-normal">
                {uiLabels.perItem}
              </span>
            </div>
          </div>
        </div>

        {/* Items List */}
        <div className="space-y-0">
          {items.map((row, index) => {
            const qty = getFieldValue(row, 'qty') || getFieldValue(row, 'quantity') || 0;
            const rate = getFieldValue(row, 'rate') || getFieldValue(row, 'price') || 0;
            const priceListRate = getFieldValue(row, 'price_list_rate') || 0; // Original price from price list
            const amount = getFieldValue(row, 'amount') || getFieldValue(row, 'total') || 0;
            const isFirst = index === 0;
            const isLast = index === items.length - 1;

            // Addon row detection — personalization service items linked to a parent
            const parentRowName = (row as ItemRow).customization_parent_row;
            if (parentRowName) {
              const parentRow = items.find((r) => r.name === parentRowName);
              const parentName = parentRow
                ? (getFieldValue(parentRow, 'item_name') || getFieldValue(parentRow, 'item_code') || '')
                : '';
              return (
                <React.Fragment key={row.idx || index}>
                  {/* Dotted separator */}
                  <div className="mx-6 sm:mx-8 border-t border-dashed border-border" />
                  <div className="flex items-center justify-between px-6 sm:px-8 py-3 pl-12 sm:pl-14">
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="text-sm text-muted-foreground">
                        ↳ {getFieldValue(row, 'item_name') || 'Personalization Service'}
                      </span>
                      {parentName && (
                        <span className="text-xs text-muted-foreground/70">
                          addon for {parentName}
                        </span>
                      )}
                    </div>
                    <span className="text-sm font-medium text-muted-foreground flex-shrink-0 ml-4">
                      {formatCurrency(Number(amount), currency)}
                    </span>
                  </div>
                  {!isLast && (
                    <div className="mx-6 sm:mx-8 border-t border-border" />
                  )}
                </React.Fragment>
              );
            }

            // Check if there's a discount (price_list_rate > rate)
            const hasDiscount = priceListRate > 0 && Number(priceListRate) > Number(rate);
            const originalPrice = priceListRate > 0 ? Number(priceListRate) : Number(rate);
            // Calculate original total (before discount) and discounted total
            const originalTotal = originalPrice * Number(qty);
            const discountedTotal = Number(amount) || 0;
            const totalDiscountAmount = hasDiscount ? (originalTotal - discountedTotal) : 0;
            const totalDiscountPercentage = hasDiscount && originalTotal > 0
              ? ((totalDiscountAmount / originalTotal) * 100).toFixed(1)
              : "0.0";

            // Phase 1: Per-item contextual information
            // Calculate percentage based on items subtotal (not grand_total)
            // This ensures percentages add up to 100% of items, not more
            const itemRank = getItemRank(row);
            const itemBundleData = bundleDataMap.get(row.name ?? '');

            return (
              <React.Fragment key={row.idx || index}>
                <div
                  className={cn(
                    "flex flex-col md:flex-row md:items-center justify-between px-6 sm:px-8 transition-all duration-150",
                    "min-h-[76px] md:min-h-0", // Good touch target size
                    isFirst ? "pt-6 sm:pt-7 pb-5" : isLast ? "py-6 sm:py-7 pb-6 sm:pb-7" : "py-6 sm:py-7",
                    // No opacity reduction for disabled/read-only items — items should look normal regardless of edit state
                  )}
                >
                  {/* Product Info - Left Side */}
                  <div className="flex-1 min-w-0">
                    {renderProductInfo(row as ItemRow, getFieldValue, toAbsoluteUrl, itemRank, resolvedLabels.itemCode, itemBundleData?.isBundle)}
                  </div>

                  {/* Right Side: Quantity, Rate, Total, and Actions */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 flex-shrink-0 md:ml-8 mt-4 md:mt-0">
                    {/* Quantity Stepper - Editable for new orders */}
                    <div className="flex flex-col items-end md:items-center space-y-1">
                      <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                        {resolvedLabels.qty}
                      </span>
                      {isNew && !disabled ? (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 rounded-full"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQuantityChange(index, Math.max(0, Number(qty) - 1));
                            }}
                            disabled={Number(qty) <= 0}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <Input
                            type="number"
                            value={qty}
                            onChange={(e) => {
                              e.stopPropagation();
                              const newQty = parseFloat(e.target.value) || 0;
                              handleQuantityChange(index, newQty);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-16 h-8 text-center text-base font-medium [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            min={0}
                          />
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 rounded-full"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQuantityChange(index, Number(qty) + 1);
                            }}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-base font-medium text-gray-900 dark:text-gray-100 md:min-w-[3rem] md:text-center">
                          {formatNumber(qty)}
                        </span>
                      )}
                    </div>

                    {/* Rate Input - Editable for new orders */}
                    {isNew && !disabled && (
                      <div className="flex flex-col items-end md:items-center space-y-1">
                        <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                          {uiLabels.rate}
                        </span>
                        <Input
                          type="number"
                          value={editingRates.has(index) ? editingRates.get(index) : rate}
                          onChange={(e) => {
                            e.stopPropagation();
                            const value = e.target.value;
                            setEditingRates(prev => {
                              const newMap = new Map(prev);
                              newMap.set(index, value);
                              return newMap;
                            });
                          }}
                          onBlur={(e) => {
                            const newRate = parseFloat(e.target.value) || 0;
                            setEditingRates(prev => {
                              const newMap = new Map(prev);
                              newMap.delete(index);
                              return newMap;
                            });
                            handleRateChange(index, newRate);
                          }}
                          onFocus={() => {
                            setEditingRates(prev => {
                              const newMap = new Map(prev);
                              newMap.set(index, String(rate));
                              return newMap;
                            });
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-24 h-8 text-right text-base font-medium [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          min={0}
                          step={0.01}
                        />
                      </div>
                    )}

                    {/* Total - Prominent with discount info */}
                    <div className="flex flex-col items-end space-y-1 md:min-w-[8rem]">
                      <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                        {resolvedLabels.amount}
                      </span>
                      {hasDiscount ? (
                        <div className="flex flex-col items-end space-y-1">
                          {/* Original Total (strikethrough) */}
                          <span className="text-xs text-gray-500 dark:text-gray-400 line-through">
                            {formatCurrency(originalTotal, currency)}
                          </span>
                          {/* Final Total - Primary */}
                          <span className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                            {formatCurrency(discountedTotal, currency)}
                          </span>
                          {/* Discount Info - Contextual */}
                          <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                            -{formatCurrency(totalDiscountAmount, currency)} ({totalDiscountPercentage}%)
                          </span>
                        </div>
                      ) : (
                        <span className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                          {formatCurrency(discountedTotal, currency)}
                        </span>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-1">
                      {/* More details button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={(e) => handleOpenDetails(index, e)}
                        title={uiLabels.moreDetails}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>

                      {/* Remove button - Only shown for new orders */}
                      {isNew && !disabled && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          onClick={(e) => handleRemoveItem(index, e)}
                          title={uiLabels.removeItem}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bundle sub-items section */}
                {itemBundleData?.isBundle && itemBundleData.packedItems.length > 0 && (
                  <div className="px-6 sm:px-8 pb-4">
                    <OrderItemBundleSection packedItems={itemBundleData.packedItems} variantSelections={itemBundleData.variantSelections} />
                  </div>
                )}

                {/* Personalization card */}
                {itemBundleData?.customizations && itemBundleData.customizations.length > 0 && (
                  <div className="px-6 sm:px-8 pb-4">
                    <OrderItemPersonalizationCard
                      customizations={itemBundleData.customizations}
                      packedItems={itemBundleData.packedItems}
                      salesOrderName={formData.name as string | undefined}
                      isSubmitted={(formData.docstatus as number) >= 1}
                    />
                  </div>
                )}

                {/* Divider - between items, not after last */}
                {!isLast && (
                  <div className="mx-6 sm:mx-8 border-t border-border" />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Divider before totals */}
        <div className="border-t border-border mx-6 sm:mx-8" />

        {/* Totals Section - Apple-style, prominent */}
        <div className="px-6 sm:px-8 py-6 sm:py-7 space-y-2.5 bg-muted/50">
          {/* Items Subtotal (before discounts) */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">{uiLabels.itemsSubtotal}</span>
              <span className="text-xs text-muted-foreground font-normal">
                {uiLabels.beforeDiscounts}
              </span>
            </div>
            <span className="text-[17px] font-medium text-gray-900 dark:text-gray-100">
              {formatCurrency(originalSubtotalBeforeDiscounts, currency)}
            </span>
          </div>

          {/* Item Discount (if present) */}
          {itemDiscountAmount > 0 && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">{resolvedLabels.discountAmount}</span>
                <span className="text-xs text-muted-foreground font-normal">
                  {uiLabels.appliedToItems}
                </span>
              </div>
              <span className="text-[17px] font-medium text-red-600 dark:text-red-400">
                -{formatCurrency(itemDiscountAmount, currency)}
              </span>
            </div>
          )}

          {/* Items Total (after item discount) - Extra prominent */}
          <div className="flex items-center justify-between pt-2.5 border-t border-border">
            <div className="flex items-center gap-2">
              <span className="text-[17px] font-bold text-gray-900 dark:text-gray-100">{uiLabels.itemsTotal}</span>
              {/* Phase 1: Contextual label - shows what % after item discount */}
              {itemDiscountAmount > 0 && (
                <span className="text-xs text-muted-foreground font-normal">
                  {itemsTotalPercentage}% {uiLabels.afterDiscount}
                </span>
              )}
            </div>
            <span className="text-[20px] font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(itemsTotalAfterDiscount, currency)}
            </span>
          </div>
        </div>
      </div>
      )}

      {/* Edit Modal */}
      {editingRow && childSchema && (
        <ChildRowEditModal
          open={modalOpen}
          onOpenChange={(open) => {
            setModalOpen(open);
            if (!open) setEditingRowIndex(null);
          }}
          rowData={editingRow}
          onRowChange={handleModalRowChange}
          childMeta={childSchema}
          disabled={disabled}
          resetKey={resetKey}
        />
      )}

      {/* Pricing Rule Violation Warning Dialog */}
      <Dialog open={pricingWarning?.show || false} onOpenChange={(open: boolean) => !open && setPricingWarning(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{uiLabels.pricingRuleViolation}</DialogTitle>
            <DialogDescription>
              {pricingWarning?.message || uiLabels.pricingRuleMessage}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setPricingWarning(null)}>
              {uiLabels.ok}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customization Form Modal — shown when adding an item with a template */}
      <CustomizationFormModal
        itemCode={pendingItem?.itemCode ?? null}
        itemName={pendingItem?.itemDetails.item_name}
        company={formData.company}
        open={!!pendingItem}
        onSubmit={(customizations) => {
          if (pendingItem) {
            finalizeAddItem(pendingItem.itemCode, pendingItem.itemDetails, customizations);
          }
        }}
        onSkip={() => {
          if (pendingItem) {
            finalizeAddItem(pendingItem.itemCode, pendingItem.itemDetails);
          }
        }}
      />

      {/* POS Product Picker Modal */}
      <POSProductPickerModal
        open={isPOSModalOpen}
        onOpenChange={setIsPOSModalOpen}
        onSelectItem={handleAddItemFromPOS}
        onRemoveItem={handleRemoveItemByCode}
        selectedItemCodes={selectedItemCodes}
        cartItems={items}
        currency={currency}
        company={formData.company}
      />
    </>
  );
}

